/**
 * useTutorApplicationProfileState — le profil de la candidature : ses questions
 * (`profileQuestions`, les spécialités et les langues lues dans les matières servies), l'envoi
 * (`submitPersonalInfos`, un seul à la fois, comme la garde de l'ancien `onSubmit`) et la fin
 * d'étape du champion de l'accueil une fois envoyé.
 */
import { useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { tutorsService } from "../../../../../../data/services/tutorsService"
import {
    type ApplicationSchoolSubject,
    profileQuestions,
    toPersonalInfosPayload,
} from "../../../../adapters/tutor/Application/tutorApplicationQuestions"
import { PROFILE_DONE_TEXT } from "../../../../adapters/tutor/Application/tutorApplicationAdapter"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { selectCandidate } from "./candidateState"
import { useTutorApplicationForm } from "./useTutorApplicationForm"

export const PROFILE_EXIT_MESSAGE = "Les données entrées seront perdues."

/** Le repli d'un refus sans phrase du serveur (une panne réseau ouvre déjà la boîte de l'app). */
export const APPLICATION_SUBMIT_FALLBACK = "Une erreur est survenue, réessaie."

// Les `defaultValue` de l'ancien formulaire qui peuvent partir tels quels : aucune langue (`[]`), et
// le SIRET vide (`""`) quand l'interrupteur est allumé sans numéro.
const PROFILE_DEFAULTS = { foreign_languages: [], num_SIRET: "" }

type ProfileState = {
    data?: {
        staticData?: { school_subjects?: ApplicationSchoolSubject[] | null }
    }
}

export function useTutorApplicationProfileState() {
    const schoolSubjects = useSelector((state: ProfileState) => state?.data?.staticData?.school_subjects ?? null)
    const userId = useSelector((state: unknown) => selectCandidate(state)?.id ?? null)
    const [serverError, setServerError] = useState<string | null>(null)
    const markDoneRef = useRef<() => void>(() => undefined)

    const mutation = useApiMutation({
        service: tutorsService.submitPersonalInfos,
        gesture: "application.profile",
        surface: "tutor_application",
        skipWhilePending: true,
        fallback: APPLICATION_SUBMIT_FALLBACK,
        onSuccess: () => markDoneRef.current(),
        onError: (message) => setServerError(message),
    })

    const questions = useMemo(
        () => profileQuestions({ currentYear: new Date().getFullYear(), schoolSubjects }),
        [schoolSubjects]
    )

    const form = useTutorApplicationForm({
        questions,
        formId: "application-personal-infos",
        entryId: userId,
        exitMessage: PROFILE_EXIT_MESSAGE,
        alwaysConfirmExit: false,
        defaults: PROFILE_DEFAULTS,
        schoolSubjects,
        submit: (state) => {
            setServerError(null)
            return mutation.start({}, toPersonalInfosPayload(state.values, state.toggles))
        },
        submitting: mutation.requesting,
        serverError,
        stepDone: { champion: "abou", text: PROFILE_DONE_TEXT },
    })
    markDoneRef.current = form.markDone

    return form
}
